import { queryOptions } from "@tanstack/react-query";

import { placeAddress, quoteByPlaces } from "@/lib/maps.functions";

/**
 * Eén gedeelde prijsbron voor de hele boekingsflow. Zowel het zoekformulier
 * (bovenaan) als het boekingsformulier gebruiken deze query-opties, zodat een
 * al berekende prijs uit de cache komt en dezelfde rit nooit twee keer wordt
 * opgevraagd. De prijsberekening zelf blijft volledig server-side in
 * `quoteByPlaces` (centrale pricing engine).
 */
export type QuoteArgs = {
  fromPlaceId: string;
  toPlaceId: string;
  passengers?: number | undefined;
  vehicleClass?: "sedan" | "bus" | undefined;
  stopPlaceIds?: string[] | undefined;
  /** Melding wanneer de routeberekening te lang duurt (per taal). */
  timeoutMessage?: string | undefined;
};

const QUOTE_TIMEOUT_MS = 20_000;

/** Laat een verzoek nooit eindeloos hangen: na de timeout volgt een nette fout. */
async function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export function placesQuoteOptions(args: QuoteArgs) {
  const passengers = args.passengers ?? 1;
  // Een busje wordt server-side al afgedwongen vanaf 5 personen; we normaliseren
  // de sleutel zodat zoekformulier en boekingsformulier dezelfde cache raken.
  const vehicleClass: "sedan" | "bus" =
    args.vehicleClass === "bus" || passengers >= 5 ? "bus" : "sedan";
  const stops = args.stopPlaceIds ?? [];
  const message = args.timeoutMessage ?? "De prijsberekening duurde te lang. Probeer het opnieuw.";
  return queryOptions({
    queryKey: [
      "places-quote",
      args.fromPlaceId,
      args.toPlaceId,
      stops.join("|"),
      passengers,
      vehicleClass,
    ] as const,
    queryFn: () =>
      withTimeout(
        quoteByPlaces({
          data: {
            fromPlaceId: args.fromPlaceId,
            toPlaceId: args.toPlaceId,
            passengers,
            ...(vehicleClass === "bus" ? { vehicleClass: "bus" as const } : {}),
            ...(stops.length > 0 ? { stopPlaceIds: stops } : {}),
          },
        }),
        QUOTE_TIMEOUT_MS,
        message,
      ),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
    retryDelay: (attempt: number) => Math.min(3000, 600 * 2 ** attempt),
  });
}

/** Adresdetails van een gekozen plaats; wordt door de hele flow hergebruikt. */
export function placeAddressOptions(placeId: string) {
  return queryOptions({
    queryKey: ["place-address", placeId] as const,
    queryFn: () => withTimeout(placeAddress({ data: { placeId } }), QUOTE_TIMEOUT_MS, "timeout"),
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });
}
